import moment, { Moment } from "moment"

import { GEO_PICKER_GLOBAL } from "components/geoPicker"
import { CommunityEventConfigType, CommunityEventGoal, CommunityEventShell } from "domains/managingJson/store/types"
import { ALL_COUNTRIES, EXPLICIT_ZONE_PATTERN, STORED_DATE_FORMAT } from "./constants"
import { emptyGoal } from "./defaults"
import { CommunityEventFormInitialValues, CommunityEventFormValues, GoalFormValues } from "./types"

const toUtcMoment = (value?: string): Moment | undefined => {
  const date = value ? moment.utc(value) : undefined

  return date?.isValid() ? date : undefined
}

const toStoredDate = (value: Moment, stored?: string): string => {
  const keepsStored = stored && EXPLICIT_ZONE_PATTERN.test(stored) && toUtcMoment(stored)?.isSame(value)

  return keepsStored ? stored : value.clone().utc().format(STORED_DATE_FORMAT)
}

const toTrimmedOrUndefined = (value?: string): string | undefined => value?.trim() || undefined

export const toCountryCode = (value: string): string => value.trim().toUpperCase()

const isSole = (values: readonly unknown[], matches: (value: unknown) => boolean): boolean => values.length === 1 && matches(values[0])

export const isAllCountries = (value: unknown): boolean => typeof value === "string" && toCountryCode(value) === ALL_COUNTRIES

export const isEveryCountryPick = (countries: string[]): boolean => isSole(countries, value => value === GEO_PICKER_GLOBAL)

const toStoredCountries = (countries: string[]): string[] => {
  return isEveryCountryPick(countries) ? [ALL_COUNTRIES] : countries.map(toCountryCode)
}

const toPickerCountries = (countries: string[]): string[] => {
  return isSole(countries, isAllCountries) ? [GEO_PICKER_GLOBAL] : countries
}

const toGoal = ({ goalId, title, target, minContribution, sources, milestones }: GoalFormValues): CommunityEventGoal => ({
  goalId,
  title: toTrimmedOrUndefined(title),
  target,
  minContribution: minContribution ?? undefined,
  sources: sources.map(({ gameId, metrics }) => ({ gameId, metrics })),
  milestones: milestones.map(({ pct, reward }) => ({ pct, reward: { type: reward.type.trim(), amount: reward.amount } })),
})

export const communityEventToForm = (
  name: string | undefined,
  data?: CommunityEventConfigType,
): CommunityEventFormInitialValues => ({
  name,
  enabled: data?.enabled === true,
  claimsEnabled: data?.claimsEnabled === true,
  title: data?.title,
  theme: data?.theme,
  startAt: toUtcMoment(data?.startAt),
  endAt: toUtcMoment(data?.endAt),
  visibleUntil: toUtcMoment(data?.visibleUntil),
  info: data?.info,
  countries: toPickerCountries(data?.countries ?? []),
  goals: data?.goals?.length ? data.goals : [emptyGoal()],
  shell: data?.shell ?? {},
})

export const formToCommunityEvent = (
  values: CommunityEventFormValues,
  existing?: CommunityEventConfigType,
): CommunityEventConfigType => {
  const shell = Object.entries(values.shell ?? {}).reduce<CommunityEventShell>(
    (result, [key, value]) => ({ ...result, [key]: toTrimmedOrUndefined(value) }),
    { ...existing?.shell },
  )

  return {
    ...existing,
    enabled: values.enabled,
    claimsEnabled: values.claimsEnabled,
    title: values.title.trim(),
    theme: toTrimmedOrUndefined(values.theme),
    startAt: toStoredDate(values.startAt, existing?.startAt),
    endAt: toStoredDate(values.endAt, existing?.endAt),
    visibleUntil: values.visibleUntil ? toStoredDate(values.visibleUntil, existing?.visibleUntil) : undefined,
    info: toTrimmedOrUndefined(values.info),
    countries: toStoredCountries(values.countries),
    goals: values.goals.map(toGoal),
    shell,
  }
}
